import React, { useEffect, useState, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import {
  Container,
  Paper,
  Typography,
  Grid,
  Box,
  Divider,
  Button,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Breadcrumbs,
  Link,
  Alert,
  CircularProgress,
  Card,
  CardContent,
} from '@mui/material';
import {
  ArrowBack as ArrowBackIcon,
  Home as HomeIcon,
  Download as DownloadIcon,
} from '@mui/icons-material';
import { useAuthenticatedFetch } from '../../hooks/useAuthenticatedFetch';
import { formatCurrency } from '../../utils/formatters';
import { API_URL } from '../../utils/config';

interface InvoiceLineItem {
  id?: string;
  lineId?: string;
  lineNumber: number;
  detailType?: string;
  description: string;
  amount: number;
  quantity: number;
  unitPrice: number;
  itemName?: string;
  taxAmount?: number;
  taxRate?: number;
  taxCodeId?: string;
  discountAmount?: number;
  discountRate?: number;
}

interface RelatedBillSummary {
  id: string;
  billNumber?: string;
  vendor?: string;
  totalAmount?: number;
  balanceDue?: number;
  dueDate?: string;
  mappingReason?: string;
}

interface InvoiceDetail {
  id: string;
  invoiceNumber: string;
  transactionDate: string;
  dueDate: string;
  customerId: string;
  customerName: string;
  customer?: {
    id?: string;
    name?: string;
    company?: string;
    email?: string;
    phone?: string;
  };
  totalAmount: number;
  subtotalAmount?: number;
  taxAmount?: number;
  balanceDue: number;
  status: string;
  currencyCode: string;
  billingEmail?: string;
  shipDate?: string;
  trackingNumber?: string;
  paymentMethod?: string;
  privateNote?: string;
  customerMemo?: string;
  createdTime: string;
  lastUpdatedTime: string;
  billingAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  shippingAddress?: {
    line1?: string;
    line2?: string;
    city?: string;
    state?: string;
    postalCode?: string;
    country?: string;
  };
  lineItems?: InvoiceLineItem[];
  relatedBill?: RelatedBillSummary | null;
}

export const InvoiceDetail: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { authenticatedFetch } = useAuthenticatedFetch();
  
  const [invoice, setInvoice] = useState<InvoiceDetail | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [isVoiding, setIsVoiding] = useState(false);
  
  // Use refs to track fetch state - Pattern 1 from react-patterns.md
  const hasFetchedRef = useRef(false);
  const lastFetchedIdRef = useRef<string | null>(null);

  useEffect(() => {
    if (!id) return;
    
    // Check if ID changed
    if (lastFetchedIdRef.current !== id) {
      lastFetchedIdRef.current = id;
      hasFetchedRef.current = false;
    }
    
    // Prevent duplicate fetches
    if (hasFetchedRef.current) {
      return;
    }
    hasFetchedRef.current = true;
    
    const fetchInvoiceDetail = async () => {
      setIsLoading(true);
      setError(null);
      
      try {
        const response = await authenticatedFetch(`${API_URL}/api/accounting/user/invoices/${id}`);
        if (response && response.ok) {
          const data = await response.json();
          if (data.success) {
            setInvoice(data.data);
          }
        } else {
          setError('Failed to load invoice details');
        }
      } catch (err) {
        setError('Failed to load invoice details. Please try again later.');
        console.error('Error fetching invoice detail:', err);
      } finally {
        setIsLoading(false);
      }
    };
    
    fetchInvoiceDetail();
  }, [id, authenticatedFetch]);

  const getStatusColor = (status?: string) => {
    const normalized = typeof status === 'string' ? status.toLowerCase() : '';
    switch (normalized) {
      case 'paid':
        return 'success';
      case 'overdue':
        return 'error';
      case 'open':
      case 'pending':
        return 'warning';
      default:
        return 'default';
    }
  };

  const isOverdue = (dueDate?: string, balanceDue?: number) => {
    if (!dueDate || typeof balanceDue !== 'number') return false;
    return new Date(dueDate) < new Date() && balanceDue > 0;
  };

  const handleDownload = async () => {
    try {
      if (!id) return;
      const response = await authenticatedFetch(`${API_URL}/api/accounting/user/invoices/${id}/pdf`);
      if (!response || !response.ok) {
        console.error('Failed to download PDF');
        return;
      }
      const blob = await response.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `invoice-${id}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      window.URL.revokeObjectURL(url);
    } catch (err) {
      console.error('Error downloading invoice PDF:', err);
    }
  };

  if (isLoading) {
    return (
      <Container
        maxWidth="lg"
        sx={{ py: 4, minHeight: '60vh', display: 'flex', alignItems: 'center', justifyContent: 'center' }}
      >
        <CircularProgress />
      </Container>
    );
  }

  if (error || !invoice) {
    return (
      <Container maxWidth="lg" sx={{ py: 4 }}>
        <Alert severity="error">{error || 'Invoice not found'}</Alert>
        <Button
          startIcon={<ArrowBackIcon />}
          onClick={() => navigate('/accounting/invoices')}
          sx={{ mt: 2 }}
        >
          Back to Invoices
        </Button>
      </Container>
    );
  }

  const discountsTotal = Array.isArray(invoice.lineItems)
    ? invoice.lineItems
        .filter(li => li.detailType === 'DiscountLineDetail')
        .reduce((sum, li) => {
          const discount = (li.discountAmount != null)
            ? Number(li.discountAmount)
            : Math.abs(Number(li.amount) || 0);
          return sum + (isNaN(discount) ? 0 : discount);
        }, 0)
    : 0;

  const amountPaid = Math.max(0, (invoice.totalAmount || 0) - (invoice.balanceDue || 0));

  return (
    <Container maxWidth="lg" sx={{ py: 4 }}>
      {/* Breadcrumb */}
      <Breadcrumbs sx={{ mb: 2 }}>
        <Link
          color="inherit"
          sx={{ display: 'flex', alignItems: 'center', cursor: 'pointer' }}
          onClick={() => navigate('/')}
        >
          <HomeIcon sx={{ mr: 0.5 }} fontSize="small" />
          Home
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/accounting/vendor')}
        >
          Subsidiary Accounting
        </Link>
        <Link
          color="inherit"
          sx={{ cursor: 'pointer' }}
          onClick={() => navigate('/accounting/invoices')}
        >
          Invoices
        </Link>
        <Typography color="text.primary">{invoice.invoiceNumber}</Typography>
      </Breadcrumbs>

      {/* Header */}
      <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
        <Box>
          <Typography variant="h4" gutterBottom>
            Invoice {invoice.invoiceNumber}
          </Typography>
          <Chip 
            label={invoice.status} 
            color={getStatusColor(invoice.status) as any}
            sx={{ mr: 1 }}
          />
          {isOverdue(invoice.dueDate, invoice.balanceDue) && (
            <Chip 
              label="OVERDUE" 
              color="error"
            />
          )}
        </Box>
        <Box>
          <Button
            startIcon={<ArrowBackIcon />}
            onClick={() => navigate('/accounting/invoices')}
            sx={{ mr: 1 }}
          >
            Back
          </Button>
          {invoice.status && invoice.status.toLowerCase() === 'open' && amountPaid === 0 && (
            <Button
              variant="contained"
              color="warning"
              sx={{ mr: 1 }}
              onClick={async () => {
              setIsVoiding(true);
              const reason = window.prompt('Please enter a reason for voiding this invoice (optional):', '');
              if (reason === null) return;
              try {
                if (!id) return;
                const response = await authenticatedFetch(`${API_URL}/api/accounting/user/invoices/${id}/void`, {
                  method: 'POST',
                  headers: { 'Content-Type': 'application/json' },
                  body: JSON.stringify({ reason })
                });
                if (!response || !response.ok) {
                  console.error('Failed to void invoice');
                  return;
                }
                // Refresh invoice details
                const res = await authenticatedFetch(`${API_URL}/api/accounting/user/invoices/${id}`);
                if (res && res.ok) {
                  const data = await res.json();
                  if (data.success) setInvoice(data.data);
                }
              } catch (err) {
                console.error('Error voiding invoice:', err);
              } finally {
                setIsVoiding(false);
              }
              }}
            >
              {isVoiding ? (
                <>
                  <CircularProgress size={16} sx={{ mr: 1 }} /> Voiding...
                </>
              ) : (
                'Void Invoice'
              )}
            </Button>
          )}
          <Button
            variant="contained"
            startIcon={<DownloadIcon />}
            onClick={handleDownload}
          >
            Download PDF
          </Button>
        </Box>
      </Box>

      <Grid container spacing={3}>
        {/* Main Invoice Details */}
        <Grid item xs={12} md={8}>
          <Paper sx={{ p: 3 }}>
            <Typography variant="h6" gutterBottom>
              Invoice Details
            </Typography>
            <Divider sx={{ mb: 2 }} />
            
            <Grid container spacing={2}>
              <Grid item xs={12} md={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Customer
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.customer?.name || invoice.customerName}
                </Typography>
                {(invoice.customer?.company || invoice.customer?.email || invoice.customer?.phone) && (
                  <Typography variant="body2" color="text.secondary">
                    {invoice.customer?.company && (<>{invoice.customer.company}<br /></>)}
                    {invoice.customer?.email && (<>{invoice.customer.email}<br /></>)}
                    {invoice.customer?.phone && (<>{invoice.customer.phone}</>)}
                  </Typography>
                )}
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Invoice Date
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {new Date(invoice.transactionDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Due Date
                </Typography>
                <Typography 
                  variant="body1" 
                  gutterBottom
                  color={isOverdue(invoice.dueDate, invoice.balanceDue) ? 'error' : 'inherit'}
                >
                  {new Date(invoice.dueDate).toLocaleDateString()}
                </Typography>
              </Grid>
              <Grid item xs={6}>
                <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                  Currency
                </Typography>
                <Typography variant="body1" gutterBottom>
                  {invoice.currencyCode}
                </Typography>
              </Grid>
            </Grid>

            {/* Billing Address */}
            {invoice.billingAddress && (
              <>
                <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
                  Billing Address
                </Typography>
                <Typography variant="body2">
                  {invoice.billingAddress.line1}<br />
                  {invoice.billingAddress.line2 && <>{invoice.billingAddress.line2}<br /></>}
                  {invoice.billingAddress.city}, {invoice.billingAddress.state} {invoice.billingAddress.postalCode}<br />
                  {invoice.billingAddress.country}
                </Typography>
              </>
            )}

            {/* Shipping Info */}
            {(invoice.shipDate || invoice.trackingNumber) && (
              <>
                <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
                  Shipping Information
                </Typography>
                <Grid container spacing={2}>
                  {invoice.shipDate && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Ship Date
                      </Typography>
                      <Typography variant="body1">
                        {new Date(invoice.shipDate).toLocaleDateString()}
                      </Typography>
                    </Grid>
                  )}
                  {invoice.trackingNumber && (
                    <Grid item xs={6}>
                      <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                        Tracking Number
                      </Typography>
                      <Typography variant="body1">
                        {invoice.trackingNumber}
                      </Typography>
                    </Grid>
                  )}
                </Grid>
              </>
            )}

            {/* Line Items */}
            {invoice.lineItems && invoice.lineItems.length > 0 && (
              <>
                <Typography variant="h6" sx={{ mt: 3 }} gutterBottom>
                  Line Items
                </Typography>
                <TableContainer>
                  <Table size="small">
                    <TableHead>
                      <TableRow>
                        <TableCell>Item</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Qty</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.lineItems
                        .filter(item => item.detailType !== 'SubTotalLineDetail' && item.detailType !== 'DiscountLineDetail')
                        .map((item, idx) => (
                        <TableRow key={item.lineId || item.id || `${item.lineNumber}-${idx}`}>
                          <TableCell>{item.itemName || '-'}</TableCell>
                          <TableCell>{item.description}</TableCell>
                          <TableCell align="right">{item.quantity}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.unitPrice, invoice.currencyCode)}
                          </TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.detailType === 'DiscountLineDetail' ? -Math.abs(item.amount) : item.amount, invoice.currencyCode)}
                          </TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </>
            )}
          </Paper>

          {/* Related Bill */}
          {invoice.relatedBill && (
            <Paper sx={{ p: 3, mt: 3 }}>
              <Typography variant="h6" gutterBottom>
                Associated Bill
              </Typography>
              <Divider sx={{ mb: 2 }} />
              <Grid container spacing={2}>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Bill Number
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {invoice.relatedBill.billNumber || invoice.relatedBill.id}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Vendor
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {invoice.relatedBill.vendor || '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Due Date
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {invoice.relatedBill.dueDate ? new Date(invoice.relatedBill.dueDate).toLocaleDateString() : '-'}
                  </Typography>
                </Grid>
                <Grid item xs={12} md={6}>
                  <Typography variant="body2" color="text.secondary" sx={{ fontWeight: 700 }}>
                    Amount
                  </Typography>
                  <Typography variant="body1" gutterBottom>
                    {typeof invoice.relatedBill.totalAmount === 'number' ? formatCurrency(invoice.relatedBill.totalAmount, invoice.currencyCode) : '-'}
                  </Typography>
                </Grid>
              </Grid>
              <Box sx={{ mt: 2 }}>
                <Button variant="outlined" onClick={() => navigate(`/accounting/bills/${invoice.relatedBill!.id}`)}>
                  View Bill
                </Button>
              </Box>
            </Paper>
          )}
        </Grid>

        {/* Summary Sidebar */}
        <Grid item xs={12} md={4}>
          <Card>
            <CardContent>
              <Typography variant="h6" gutterBottom>
                Payment Summary
              </Typography>
              <Divider sx={{ mb: 2 }} />
              
              {invoice.subtotalAmount && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Subtotal
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(invoice.subtotalAmount, invoice.currencyCode)}
                  </Typography>
                </Box>
              )}
              
              {discountsTotal > 0 && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Discounts
                  </Typography>
                  <Typography variant="body2">
                    -{formatCurrency(discountsTotal, invoice.currencyCode)}
                  </Typography>
                </Box>
              )}

              {invoice.taxAmount && (
                <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                  <Typography variant="body2" color="text.secondary">
                    Tax
                  </Typography>
                  <Typography variant="body2">
                    {formatCurrency(invoice.taxAmount, invoice.currencyCode)}
                  </Typography>
                </Box>
              )}
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                <Typography variant="body1" fontWeight="bold">
                  Total Amount
                </Typography>
                <Typography variant="body1" fontWeight="bold">
                  {formatCurrency(invoice.totalAmount, invoice.currencyCode)}
                </Typography>
              </Box>
              
              <Divider sx={{ mb: 2 }} />
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                <Typography variant="body2" color="text.secondary">
                  Amount Paid
                </Typography>
                <Typography variant="body2">
                  {formatCurrency(invoice.totalAmount - invoice.balanceDue, invoice.currencyCode)}
                </Typography>
              </Box>
              
              <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                <Typography variant="h6" color={invoice.balanceDue > 0 ? 'error' : 'success.main'}>
                  Balance Due
                </Typography>
                <Typography variant="h6" color={invoice.balanceDue > 0 ? 'error' : 'success.main'}>
                  {formatCurrency(invoice.balanceDue, invoice.currencyCode)}
                </Typography>
              </Box>

              {invoice.paymentMethod && (
                <>
                  <Divider sx={{ my: 2 }} />
                  <Typography variant="body2" color="text.secondary">
                    Payment Method
                  </Typography>
                  <Typography variant="body1">
                    {invoice.paymentMethod}
                  </Typography>
                </>
              )}
            </CardContent>
          </Card>
        </Grid>
      </Grid>
    </Container>
  );
};